// NGRP Founding Vision content — authored by the party leadership.
// This is served client-side (English) and referenced from the site's
// Vision/About/Agenda pages. Long-form ideological content is intentionally
// kept in English to preserve tone and legal precision.

export const FOUNDING_VISION = {
  title: "From Political Democracy to High-Performance Governance",
  lede: [
    "India does not need weaker democracy.",
    "India needs democracy that delivers better governance.",
  ],
  intro:
    "Our Constitution created one of the world's largest democratic systems. India has built strong institutions, conducted elections at enormous scale, developed world-class capabilities and lifted millions of citizens into new economic opportunities. But India's aspirations have grown. The governance system must now evolve with them.",

  why_reform: {
    heading: "Why India's Governance Needs Reform",
    strengths: [
      "India has talent.",
      "India has capital.",
      "India has technology.",
      "India has natural resources.",
      "India has an enormous young population.",
      "India has democratic legitimacy.",
    ],
    questions: [
      "Why should a citizen have to repeatedly visit an office for a service that can be delivered transparently and digitally?",
      "Why should an entrepreneur spend months navigating permissions?",
      "Why should a farmer struggle to understand which department is responsible for his problem?",
      "Why should a student need political influence for something that should be available as a right?",
      "Why should a contractor, supplier or small business have to continuously pursue legitimately certified government payments?",
      "Why should an honest government officer struggle inside a system that sometimes rewards procedural avoidance more than measurable results?",
    ],
    closing:
      "These are not merely individual complaints. They are symptoms of institutional friction.",
  },

  from_to_transitions: [
    { from: "File-based governance", to: "Outcome-based governance" },
    { from: "Discretion", to: "Transparent rules" },
    { from: "Repeated applications", to: "Digital integration" },
    { from: "Department-centric administration", to: "Citizen-centric administration" },
    { from: "Uncertain timelines", to: "Time-bound services" },
    { from: "Activity reporting", to: "Outcome measurement" },
    { from: "Reactive governance", to: "Data-driven governance" },
    { from: "Political intervention", to: "Institutional processes" },
  ],

  measurable_performance: {
    heading: "Government Should Have Measurable Performance",
    intro:
      "Imagine if citizens could see: how long a department takes, how many applications are pending, how many projects are delayed, how much was budgeted, how much was spent, what outcomes were produced and who is responsible for unresolved delays. This should be normal democratic information.",
    domains: [
      {
        area: "Infrastructure",
        metrics: [
          "Cost",
          "Timeline",
          "Physical Progress",
          "Financial Progress",
          "Quality",
          "Contractor Payment Status",
        ],
      },
      {
        area: "Healthcare",
        metrics: [
          "Doctors",
          "Beds",
          "Medicine availability",
          "Waiting time",
          "Patient outcomes",
        ],
      },
      {
        area: "Education",
        metrics: [
          "Attendance",
          "Learning outcomes",
          "Teacher availability",
          "Infrastructure",
        ],
      },
      {
        area: "Citizen Services",
        metrics: [
          "Applications",
          "Average processing time",
          "Pendency",
          "Rejections",
          "Appeals",
        ],
      },
    ],
    closing:
      "Transparency is not thousands of unreadable PDFs. Transparency means a citizen can understand what government promised, what it spent and what it delivered.",
  },

  formula:
    "Knowledge + Integrity + Ground Work + Performance + Public Trust = Leadership",

  principles: [
    {
      number: 1,
      title: "No Individual Above the Institution",
      body:
        "Founder, President, Minister, MP, MLA, District President, Village Coordinator — everyone operates under the party constitution. NGRP must become an institution capable of surviving its founders.",
    },
    {
      number: 2,
      title: "Leadership Must Be Earned",
      body:
        "Political positions should not automatically come from money, family, connections, social-media popularity or simply spending years around senior leaders. Leadership should emerge from integrity, competence, public work, performance, community trust and the ability to lead.",
    },
    {
      number: 3,
      title: "Transparent Candidate Selection",
      body:
        "Election tickets should not be handed out behind closed doors. NGRP will work toward a structured candidate pipeline: Open Application → Eligibility & Background Verification → Integrity Review → Public-Service Record → Governance & Constitutional Assessment → Constituency Performance → Local Feedback → Independent Evaluation → Candidate Selection. Money should not purchase political candidature.",
    },
    {
      number: 4,
      title: "Performance-Based Organisation",
      body:
        "Every office-bearer should have responsibilities. A district leader should be able to explain what organisation was built, what public issues were documented, what initiatives were undertaken, what outcomes were achieved and how money was received and spent. Political titles without responsibility should gradually disappear from NGRP culture.",
    },
    {
      number: 5,
      title: "Decentralisation",
      body:
        "India cannot be effectively governed entirely from Delhi. Solve the problem at the lowest competent level — village, taluka, district, state, Union — in that order. Higher authority should support local institutions, not unnecessarily replace them.",
    },
    {
      number: 6,
      title: "Technology With Accountability",
      body:
        "Digitising a bad process does not automatically create good governance. Our sequence: Simplify → Standardise → Digitise → Measure → Audit → Improve. NGRP advocates interoperable digital government systems, transparent tracking, time-bound approvals, automated alerts and public performance dashboards — while protecting citizens' privacy and data.",
    },
    {
      number: 7,
      title: "Zero-Tolerance Culture Toward Corruption",
      body:
        "The objective is not just catching corruption after it happens — it is designing systems where corruption becomes difficult. Reduced discretion, digital trails, transparent procurement, time-bound decisions, independent audits, conflict-of-interest rules, whistleblower safeguards and clear accountability. Prevent through system design, detect through transparency, punish through due process.",
    },
    {
      number: 8,
      title: "Evidence Before Populism",
      body:
        "Every major NGRP proposal should answer: what problem are we solving, how many will benefit, what will it cost, where will the money come from, what are the unintended consequences, how will implementation work, and how will success be measured? Welfare protects those who need support; sustainable prosperity requires education, skills, jobs, agriculture productivity, entrepreneurship, infrastructure, investment and innovation.",
    },
    {
      number: 9,
      title: "Government as a Service Platform",
      body:
        "One Citizen — One Government Interface. A citizen should not need to know the internal structure of government to receive a service. Technology routes the request to the correct authority; the citizen sees an application number, responsible authority, expected completion date, current status, reason for delay, escalation and appeal mechanisms. Citizens should not physically carry files between departments.",
    },
    {
      number: 10,
      title: "From Manifesto to Performance Contract",
      body:
        "Traditional politics says 'here are our promises'. NGRP says 'here are our targets, timelines, budgets and measurements'. Every major commitment should have: Baseline → Target → Budget → Responsible authority → Deadline → KPI → Public progress report. This turns a manifesto into a Public Performance Contract.",
    },
  ],

  call: {
    heading: "Our Call",
    items: [
      { audience: "Every UPSC and MPSC aspirant", body: "Your knowledge can serve India beyond an examination." },
      { audience: "Every engineer", body: "Help us redesign broken systems." },
      { audience: "Every doctor", body: "Help us build better public healthcare." },
      { audience: "Every farmer", body: "Help us reform agricultural policy from the ground." },
      { audience: "Every entrepreneur", body: "Help us create an economy where enterprise generates opportunity." },
      { audience: "Every teacher", body: "Help us build human capital." },
      { audience: "Every woman and young citizen", body: "Leadership belongs to you too." },
      { audience: "Every honest government servant", body: "Your experience can help us understand what needs reform." },
      { audience: "Every ordinary citizen", body: "You do not have to become a politician to participate in better governance." },
    ],
  },

  message:
    "We are not saying that everything in India has failed. India has achieved extraordinary things. We are saying: India's next stage of development requires its governance institutions to become as ambitious as its people.",

  promise:
    "We will not claim that one party can solve every problem overnight. We will make a more serious promise: we will build institutions that continuously identify problems, measure performance, correct failures and remain accountable to citizens.",

  motto_chain:
    "Knowledge → Integrity → Public Service → Performance → Public Trust → Leadership",

  purpose_line:
    "Our purpose is bigger than winning an election. Our purpose is to prove that politics itself can be governed better.",

  closing_line: "Better Institutions. Better Governance. Stronger India.",
};

// ---------------------------------------------------------------------------
// Self-Correcting Governance — operationalising the founding vision
// ---------------------------------------------------------------------------
export const SELF_CORRECTING = {
  eyebrow: "Governance that corrects itself",
  title: "Citizens Should Not Have to Protest for Basic Governance",
  lede:
    "In a nation of 140 crore people, every citizen has aspirations. Achieving them should not require repeated protests, visits to government offices, bribes or political connections. A good governance system should work automatically, transparently and on time.",

  principle: {
    label: "Our principle",
    body:
      "No Protest for Routine Rights. No Bribe for Public Services. No Political Recommendation for Legitimate Work.",
  },

  intent:
    "The purpose of governance reform is not merely to solve problems after citizens become angry. The objective is to create a system that continuously detects problems, identifies delays, fixes weaknesses and prevents the same failures from happening again.",

  cycle: [
    { key: "measure", title: "Measure", body: "Track service delays, pendency, complaints and outcomes across every department." },
    { key: "detect", title: "Detect", body: "Surface abnormal delays and repeat complaints automatically, in near real time." },
    { key: "correct", title: "Correct", body: "Escalate to higher authorities and trigger corrective action — not just individual disposals." },
    { key: "verify", title: "Verify", body: "Confirm the fix actually worked for the citizen or business who reported it." },
    { key: "improve", title: "Improve", body: "Update rules, processes and staffing based on evidence, not opinion." },
    { key: "prevent", title: "Prevent Recurrence", body: "Fix the root cause so the same systemic failure never becomes normal again." },
  ],

  department_questions: [
    "Where are services getting delayed?",
    "Why are files pending?",
    "Where are citizens making repeated complaints?",
    "Where is corruption occurring?",
    "Which rules create unnecessary hurdles?",
    "Which approvals can be automated?",
    "Which officer or department is repeatedly missing deadlines?",
    "What policy is producing unintended consequences?",
  ],

  from_complaint_to_self_correcting: [
    {
      from: "Problems get serious attention only after complaints, media, court cases or protests.",
      to: "Government discovers its problems before citizens are forced to protest about them.",
    },
    {
      from: "Same complaint disposed one at a time, forever.",
      to: "Repeat complaints trigger a root-cause investigation, not another individual reply.",
    },
    {
      from: "A bad rule creates friction for thousands of citizens.",
      to: "Government examines and rectifies the rule itself.",
    },
  ],

  accountability_chain: [
    { key: "who", title: "Who is responsible", body: "A named authority, not a nameless department." },
    { key: "docs", title: "What documents are required", body: "A single published checklist per service." },
    { key: "deadline", title: "What is the deadline", body: "A statutory or published SLA in days." },
    { key: "status", title: "Current status", body: "Trackable end-to-end by the citizen, like a bank transaction." },
    { key: "reason", title: "Reason for delay", body: "If missed, the reason is public and honest." },
    { key: "escalation", title: "Escalation authority", body: "The next accountable officer, one click away." },
  ],

  goals: [
    "The success of government is not how many grievances it receives and resolves — it is how many grievances citizens never had to file.",
    "The best anti-corruption system reduces opportunities for corruption.",
    "The best grievance system prevents grievances.",
    "The best administration is one where citizens do not need political influence to obtain legitimate services.",
    "The best governance reform is not a one-time reform. It is continuous reform.",
  ],

  vision_promises: [
    "No unnecessary delay.",
    "No unnecessary human intervention.",
    "No corruption.",
    "No political recommendation for legitimate rights.",
    "No need to protest for routine public services.",
  ],

  headline: "140 Crore Aspirations. A Government That Continuously Improves.",
  tagline: "From Protest-Driven Governance to Self-Correcting Governance.",
  standard: "That should be the standard of governance for a modern India.",
};
